import { ASSIGNED_DIFFICULTIES } from '@all-for-one/shared-types'
import styles from 'css/components/tables/difficulty-pills.module.css'
import React from 'react'

interface DifficultyPillsProps {
    currentDifficulty: string
    handleDifficultyChange: (difficulty: string) => void
    includeAll?: boolean
    availableDifficulties?: string[]
    ariaLabel?: string
    /** 'segmented' (default) is the Grades / Lesson Generator look: pills
        inside a tinted container, the mockups' .tier-tabs. 'standalone' is
        Classroom Detail's .pill row -- free-standing bordered pills on the
        page with a solid azure active state, and no container tint. The two
        mockups genuinely specify different controls here; an earlier comment
        in the stylesheet claimed the segmented pattern was verified in both,
        which is not the case for Classroom Detail. */
    variant?: 'segmented' | 'standalone'
    /** How many rows sit behind each pill, keyed by difficulty ('All' for the
        total). Optional and additive: when it's absent nothing renders and the
        segmented callers are untouched, because only Classroom Detail's mockup
        puts a count on its pills (`.pill .n`). Supplying it also switches on
        her hide-empty behaviour below. */
    counts?: Record<string, number>
}

/* Mockup markup (AllForOne_ClassroomDetail_v2.html) builds each pill as a
   real <button role="tab" aria-selected>, inside a container with
   role="tablist" -- confirmed 2026-10-10 via a live click-through that this
   component's actual filtering already worked, but as plain <div onClick>
   pills: no ARIA tab semantics, and (since a div has no native keyboard
   behavior the way a button does) no keyboard operability either. Using a
   real <button> fixes both at once, matching the mockup's own choice. */
export const DifficultyPills: React.FC<DifficultyPillsProps> = ({ currentDifficulty, handleDifficultyChange, includeAll = true, availableDifficulties, ariaLabel = 'Filter students by level', variant = 'segmented', counts }) => {
	const standalone = variant === 'standalone'
	const rowClass = standalone ? styles['standalone-row'] : styles['difficulty-row']
	const pillClass = standalone ? styles['standalone-pill'] : styles['difficulty-pill']
	return (<div className={rowClass} role="tablist" aria-label={ariaLabel}>
			{includeAll && (
				<button
					type="button"
					role="tab"
					aria-selected={currentDifficulty === 'All'}
					className={`${pillClass} ${currentDifficulty === 'All' ? styles.active : ''}`}
					onClick={() => handleDifficultyChange('All')}
				>
					All
					{counts && <span className={styles.pillCount}>{counts.All ?? 0}</span>}
				</button>
			)}

		{Object.values(ASSIGNED_DIFFICULTIES).map((difficulty: string) => {
			const hasData = !availableDifficulties || availableDifficulties.includes(difficulty)
			const isActive = hasData && currentDifficulty === difficulty
			/* "a tier with nobody in it is noise -- hide it unless it is the active
			   filter", straight from the mockup's own buildPills(). Only applies
			   when counts were supplied; without them every tier still renders. */
			if (counts && !counts[difficulty] && !isActive) return null
			return (
				<button
					type="button"
					role="tab"
					aria-selected={isActive}
					key={difficulty}
					className={`${pillClass} ${isActive ? styles[`active-${difficulty.toLowerCase()}`] : ''} ${!hasData ? styles.disabled : ''}`}
					onClick={hasData ? () => handleDifficultyChange(difficulty) : undefined}
					disabled={!hasData}
				>
					{difficulty}
					{counts && <span className={styles.pillCount}>{counts[difficulty] ?? 0}</span>}
					{!hasData && <span className={styles['no-grades']}>no grades</span>}
				</button>
			)
		})}
	</div>)
}
